import { useState } from 'react';
import { trpc } from '../../../lib/trpc';
import type { ReviewType } from '@wineryxplorer/shared';

export interface UserReview {
  _id: string;
  reviewType: ReviewType;
  title: string;
  body: string;
  ratings: Record<string, number>;
  helpfulCount: number;
  notHelpfulCount: number;
  underReview: boolean;
  communityFlag?: string;
  isHidden: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
  entityId: string;
  moderationOutcome: {
    _id: string;
    decision: 'approved' | 'removed';
    reason: string;
    decidedAt: Date | string;
    flagType: 'venueManager' | 'community';
  } | null;
}

export interface UserReviewsListProps {
  className?: string;
}

// Helper to format date
const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Get review type label
const getReviewTypeLabel = (type: ReviewType): string => {
  const labels: Record<ReviewType, string> = {
    venue: 'Venue Review',
    event: 'Event Review',
    experience: 'Experience Review',
    wine: 'Wine Review',
  };
  return labels[type] || type;
};

// Get status badge for a review
const getStatusBadge = (review: UserReview) => {
  if (review.moderationOutcome?.decision === 'removed') {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
        Removed
      </span>
    );
  }

  if (review.isHidden) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
            clipRule="evenodd"
          />
          <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
        </svg>
        Hidden
      </span>
    );
  }

  if (review.underReview) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
        </svg>
        Under Review
      </span>
    );
  }

  if (review.communityFlag) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z"
            clipRule="evenodd"
          />
        </svg>
        Flagged
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
      Published
    </span>
  );
};

// Star display component (read-only)
const StarDisplay = ({ value }: { value: number }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-4 h-4 ${star <= value ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      ))}
    </div>
  );
};

// Individual review card
const UserReviewCard = ({ review }: { review: UserReview }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const maxPreviewLength = 200;
  const shouldTruncate = review.body.length > maxPreviewLength;
  const displayBody =
    shouldTruncate && !isExpanded ? `${review.body.slice(0, maxPreviewLength)}...` : review.body;

  // Get overall rating
  const overallRating =
    review.reviewType === 'wine'
      ? review.ratings.score
      : review.ratings.overall;

  return (
    <article className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">
            {getReviewTypeLabel(review.reviewType)}
          </span>
          <span className="text-gray-400">•</span>
          <time className="text-sm text-gray-500" dateTime={new Date(review.createdAt).toISOString()}>
            {formatDate(review.createdAt)}
          </time>
        </div>
        {getStatusBadge(review)}
      </div>

      {/* Rating */}
      <div className="mb-2">
        {review.reviewType === 'wine' ? (
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-gray-900">{overallRating}</span>
            <span className="text-sm text-gray-500">/ 100</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <StarDisplay value={overallRating} />
            <span className="text-sm text-gray-600">{overallRating.toFixed(1)}</span>
          </div>
        )}
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{review.title}</h3>

      {/* Body */}
      <div className="mb-3">
        <p className="text-gray-700 whitespace-pre-wrap text-sm">{displayBody}</p>
        {shouldTruncate && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-1 text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            {isExpanded ? 'Show less' : 'Read more'}
          </button>
        )}
      </div>

      {/* Helpfulness stats */}
      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
        <span className="flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
            />
          </svg>
          {review.helpfulCount} helpful
        </span>
        <span className="flex items-center gap-1">
          <svg className="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
            />
          </svg>
          {review.notHelpfulCount} not helpful
        </span>
      </div>

      {/* Moderation outcome (if removed) */}
      {review.moderationOutcome?.decision === 'removed' && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-800">
                This review was removed on {formatDate(review.moderationOutcome.decidedAt)}
              </p>
              <p className="text-sm text-red-700 mt-1">
                <span className="font-medium">Reason:</span> {review.moderationOutcome.reason}
              </p>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export const UserReviewsList = ({ className = '' }: UserReviewsListProps) => {
  const [filterType, setFilterType] = useState<ReviewType | 'all'>('all');

  const { data, isLoading, error } = trpc.userReviews.myReviews.useQuery(
    filterType === 'all' ? undefined : { reviewType: filterType }
  );

  if (isLoading) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="inline-flex items-center gap-2 text-gray-500">
          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Loading your reviews...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="inline-flex items-center gap-2 text-red-600">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          Error loading reviews: {error.message}
        </div>
      </div>
    );
  }

  const reviews = (data?.items || []) as UserReview[];

  return (
    <div className={className}>
      {/* Filter tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6 overflow-x-auto" aria-label="Review type filter">
          {(['all', 'venue', 'event', 'experience', 'wine'] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setFilterType(type)}
              className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                filterType === type
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {type === 'all' ? 'All Reviews' : getReviewTypeLabel(type)}
            </button>
          ))}
        </nav>
      </div>

      {/* Reviews list */}
      {reviews.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No reviews yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            {filterType === 'all'
              ? "You haven't written any reviews yet."
              : `You haven't written any ${filterType} reviews yet.`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <UserReviewCard key={review._id} review={review} />
          ))}
        </div>
      )}

      {/* Total count */}
      {reviews.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-center">
          Showing {reviews.length} review{reviews.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};
